import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import type { Event } from '../../types';

export default function EventDetail() {
  const { id } = useParams<{ id: string }>();
  const [event, setEvent] = useState<Event | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.from('events').select('*').eq('id', id!).single()
      .then(({ data }) => { setEvent(data); setLoading(false); });
  }, [id]);

  const formatDate = (d: string) => new Date(d).toLocaleDateString('es-MX', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit'
  });

  if (loading) return (
    <div className="min-h-screen animate-pulse" style={{ backgroundColor: '#1C1C1E' }}>
      <div className="h-64" style={{ backgroundColor: '#2C2C2E' }} />
      <div className="p-4 space-y-3">
        <div className="h-6 rounded" style={{ backgroundColor: '#2C2C2E', width: '75%' }} />
        <div className="h-4 rounded" style={{ backgroundColor: '#2C2C2E', width: '50%' }} />
      </div>
    </div>
  );

  if (!event) return (
    <div className="text-center py-16" style={{ backgroundColor: '#1C1C1E' }}>
      <p style={{ color: '#B3B3B3' }}>Evento no encontrado</p>
      <Link to="/events" className="mt-3 block" style={{ color: '#4A90D9' }}>← Volver</Link>
    </div>
  );

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#1C1C1E' }}>
      {/* Hero */}
      <div className="relative">
        {event.image_url ? (
          <img src={event.image_url} alt={event.title} className="w-full h-72 object-cover" />
        ) : (
          <div className="w-full h-40" style={{ background: 'linear-gradient(180deg, #0D2B8E 0%, #1C1C1E 100%)' }} />
        )}
        {/* Gradiente sobre imagen */}
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, #1C1C1E 0%, transparent 60%)' }} />
        {/* Botón back */}
        <Link
          to="/events"
          className="absolute top-4 left-4 w-9 h-9 flex items-center justify-center rounded-full text-white font-bold"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }}
        >
          ←
        </Link>
        {/* Título sobre imagen */}
        <div className="absolute bottom-0 left-0 right-0 px-4 pb-4">
          <h1 className="text-xl font-bold text-white leading-tight">{event.title}</h1>
        </div>
      </div>

      {/* Info */}
      <div className="p-4 space-y-3">
        <div className="rounded-lg p-4 space-y-2" style={{ backgroundColor: '#2C2C2E' }}>
          <p className="text-sm capitalize" style={{ color: '#4A90D9' }}>📅 {formatDate(event.start_time)}</p>
          {event.end_time && (
            <p className="text-sm capitalize" style={{ color: '#B3B3B3' }}>⏰ Hasta: {formatDate(event.end_time)}</p>
          )}
          {event.location && (
            <p className="text-sm" style={{ color: '#B3B3B3' }}>📍 {event.location}</p>
          )}
        </div>

        {event.description && (
          <div className="rounded-lg p-4" style={{ backgroundColor: '#2C2C2E' }}>
            <h2 className="font-semibold text-white mb-2">Descripción</h2>
            <p className="text-sm leading-relaxed" style={{ color: '#B3B3B3' }}>{event.description}</p>
          </div>
        )}
      </div>
    </div>
  );
}
